/**
 * NightModeManager — Runtime CSS Filter Application & Asset Protection
 * 
 * Applies invert(1) hue-rotate(180deg) filter to the application root when Night Mode is ON.
 * Protects canvas and image elements by applying the inverse filter to maintain color fidelity.
 * All changes are additive and browser-only; no CSS files are modified.
 */

const NIGHT_MODE_FILTER = 'invert(1) hue-rotate(180deg)';
const ASSET_PROTECTION_FILTER = 'invert(1) hue-rotate(180deg)';
const NIGHT_MODE_CLASS = 'night-mode-active';

/**
 * Apply Night Mode filter to the application root
 */
export function applyNightMode(): void {
  try {
    const root = document.documentElement;
    
    // Apply filter to root
    root.style.filter = NIGHT_MODE_FILTER;
    root.classList.add(NIGHT_MODE_CLASS);
    
    // Protect canvas elements
    const canvases = document.querySelectorAll('canvas');
    canvases.forEach((canvas) => {
      canvas.style.filter = ASSET_PROTECTION_FILTER;
    });
    
    // Protect image elements
    const images = document.querySelectorAll('img');
    images.forEach((img) => {
      img.style.filter = ASSET_PROTECTION_FILTER;
    });
    
    // Protect video elements
    const videos = document.querySelectorAll('video');
    videos.forEach((video) => {
      video.style.filter = ASSET_PROTECTION_FILTER;
    });
    
    console.log('[NightModeManager] Night Mode applied');
  } catch (error) {
    console.error('[NightModeManager] Error applying Night Mode:', error);
  }
}

/**
 * Remove Night Mode filter from the application root
 */
export function removeNightMode(): void {
  try {
    const root = document.documentElement;
    
    // Remove filter from root
    root.style.filter = '';
    root.classList.remove(NIGHT_MODE_CLASS);
    
    // Remove protection from canvas elements
    const canvases = document.querySelectorAll('canvas');
    canvases.forEach((canvas) => {
      canvas.style.filter = '';
    });
    
    // Remove protection from image elements
    const images = document.querySelectorAll('img');
    images.forEach((img) => {
      img.style.filter = '';
    });
    
    // Remove protection from video elements
    const videos = document.querySelectorAll('video');
    videos.forEach((video) => {
      video.style.filter = '';
    });
    
    console.log('[NightModeManager] Night Mode removed');
  } catch (error) {
    console.error('[NightModeManager] Error removing Night Mode:', error);
  }
}

/**
 * Toggle Night Mode on/off
 */
export function toggleNightMode(isEnabled: boolean): void {
  if (isEnabled) {
    applyNightMode();
  } else {
    removeNightMode();
  }
}

/**
 * Protect newly added assets (canvas/img/video elements)
 * Call this after dynamically adding media to the DOM
 */
export function protectNewAssets(): void {
  try {
    const root = document.documentElement;
    
    // Only apply if Night Mode is active
    if (!root.classList.contains(NIGHT_MODE_CLASS)) {
      return;
    }
    
    // Protect new canvas elements
    const canvases = document.querySelectorAll<HTMLCanvasElement>('canvas:not([style*="filter"])');
    canvases.forEach((canvas) => {
      canvas.style.filter = ASSET_PROTECTION_FILTER;
    });
    
    // Protect new image elements
    const images = document.querySelectorAll<HTMLImageElement>('img:not([style*="filter"])');
    images.forEach((img) => {
      img.style.filter = ASSET_PROTECTION_FILTER;
    });
    
    // Protect new video elements
    const videos = document.querySelectorAll<HTMLVideoElement>('video:not([style*="filter"])');
    videos.forEach((video) => {
      video.style.filter = ASSET_PROTECTION_FILTER;
    });
  } catch (error) {
    console.error('[NightModeManager] Error protecting new assets:', error);
  }
}
